import { useMemo, useState, useCallback } from 'react';

import { useGetMappingCorridorsQuery, CORRIDOR_TYPES } from '@/entities/dashboard/mapping';
import type { CorridorType, MappingCorridor } from '@/entities/dashboard/mapping';
import { useGetTransportsEnRouteQuery } from '@/entities/dashboard/transport';
import type { LocationModel } from '@/entities/location';
import { useProjectSiteParams } from '@/shared/lib';

import { createCorridorDisplay, calculateCorridorStatistics } from '../lib/corridor.utils';
import type { CorridorDisplay } from './types';

export function useMapCorridors(locations: LocationModel[], weekNo?: number, organizationId?: string) {
  const { projectId, siteId } = useProjectSiteParams();
  const [corridorType, setCorridorType] = useState<CorridorType>(CORRIDOR_TYPES.IN_TRANSIT);

  // Use new filtered endpoint for IN_TRANSIT corridors when organizationId is present
  const useFilteredEndpoint = corridorType === CORRIDOR_TYPES.IN_TRANSIT && organizationId;

  // Original mapping corridors query
  const {
    data: mappingData,
    isLoading: mappingLoading,
    error: mappingError
  } = useGetMappingCorridorsQuery(
    {
      type: corridorType,
      weekNo,
      projectId,
      siteId,
      ...(organizationId && { organizationId })
    },
    {
      skip: !!useFilteredEndpoint || !projectId || !siteId
    }
  );

  // New filtered transport en-route query
  const {
    data: transportData,
    isLoading: transportLoading,
    error: transportError
  } = useGetTransportsEnRouteQuery(
    {
      projectId: projectId!,
      siteId: siteId!,
      weekNo,
      partners: organizationId ? [organizationId] : undefined // partners parameter accepts array of organizationIds
    },
    {
      skip: !useFilteredEndpoint || !projectId || !siteId
    }
  );

  const locationsMap = useMemo(() => {
    const map = new Map<string, LocationModel>();
    locations.forEach((loc) => {
      map.set(loc.id, loc);
    });
    return map;
  }, [locations]);

  // Convert transport data to mapping corridor format
  const corridors = useMemo(() => {
    if (useFilteredEndpoint && transportData?.corridors) {
      // Map transport corridors to mapping corridor format
      return transportData.corridors
        .map(
          (corridor): MappingCorridor => ({
            from: corridor.originId,
            to: corridor.destinationId,
            consignments: '', // Not available in transport API
            daysSinceDispatch: corridor.daysSinceDispatch,
            vehicleCategory: corridor.vehicleCategory,
            dispatchDate: '', // Not available in transport API
            ltuQty: corridor.value
          })
        )
        .filter((corridor) => locationsMap.has(corridor.from) && locationsMap.has(corridor.to));
    }

    if (!mappingData?.features) return [];
    return mappingData.features.filter((corridor) => locationsMap.has(corridor.from) && locationsMap.has(corridor.to));
  }, [mappingData, transportData, locationsMap, useFilteredEndpoint]);

  const corridorDisplays = useMemo((): CorridorDisplay[] => {
    return corridors
      .map((corridor) => {
        const fromLocation = locationsMap.get(corridor.from);
        const toLocation = locationsMap.get(corridor.to);

        if (!fromLocation || !toLocation) return null;

        return createCorridorDisplay(corridor, fromLocation, toLocation);
      })
      .filter((display): display is NonNullable<typeof display> => display !== null);
  }, [corridors, locationsMap]);

  const getCorridorsForLocation = useCallback(
    (locationId: string) => {
      return corridorDisplays.filter((corridor) => corridor.from.id === locationId || corridor.to.id === locationId);
    },
    [corridorDisplays]
  );

  const statistics = useMemo(() => {
    return calculateCorridorStatistics(corridors);
  }, [corridors]);

  // Combine loading and error states
  const isLoading = useFilteredEndpoint ? transportLoading : mappingLoading;
  const error = useFilteredEndpoint ? transportError : mappingError;

  return {
    corridors,
    corridorDisplays,
    corridorType,
    setCorridorType,
    isLoading,
    error,
    statistics,
    getCorridorsForLocation
  };
}
